import { Mark, mergeAttributes } from "@tiptap/core"
import type { Attrs, Mark as ProsemirrorMark, Schema } from "@tiptap/pm/model"

import type { MediaAssetOrExternalImage } from "../../../../core/MediaLibrary/hooks/mediaLibraryData"
import { mapMediaToLinkContent } from "../../../LinkField/useLinkField"
import {
  type FileLinkData,
  type ImageLinkData,
  type LinkElement,
  type LinkElementData,
  LinkExtension,
} from "./LinkModel"

export interface LinkProtocolOptions {
  scheme: string
  optionalSlashes?: boolean
}

export interface LinkOptions {
  autolink: boolean

  protocols: (LinkProtocolOptions | string)[]

  openOnClick: boolean

  linkOnPaste: boolean

  /** These are the values passed into the HTML through the Tiptap extension. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  htmlAttributes: Record<string, any>

  validate?: (url: string) => boolean
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    Link: {
      setWebLink: (attributes: {
        href: string
        target?: string | null
      }) => ReturnType

      setDocumentLink: (attributes: { id: string }) => ReturnType

      setMediaLink: (attributes: {
        media: MediaAssetOrExternalImage
      }) => ReturnType

      unsetLink: () => ReturnType
    }
  }
}

const componentName = "hyperlink"

const TTLink = Mark.create<LinkOptions>({
  name: componentName,
  priority: 1000,
  keepOnSplit: false,
  inclusive: false,

  addOptions() {
    return {
      openOnClick: false,
      linkOnPaste: true,
      autolink: true,
      protocols: [],
      htmlAttributes: {
        target: "_blank",
        rel: "noopener noreferrer nofollow",
        class: null,
        id: "",
      },
      validate: undefined,
    }
  },

  addAttributes() {
    return {
      href: {
        default: "",
      },
      id: {
        default: "",
      },
      target: {
        default: "_blank",
      },
      rel: {
        default: "noopener noreferrer nofollow",
      },
      class: {
        default: null,
      },
      media: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [{ tag: 'a[href]:not([href *= "javascript:" i])' }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "a",
      mergeAttributes(this.options.htmlAttributes, HTMLAttributes),
      0,
    ]
  },

  addCommands() {
    return {
      setWebLink:
        (attributes) =>
        ({ chain }) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMark(this.name, attributes)
            .setMeta("preventAutolink", true)
            .run()
        },

      setDocumentLink:
        (attributes) =>
        ({ chain }) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMark(this.name, attributes)
            .setMeta("preventAutolink", true)
            .run()
        },

      setMediaLink:
        (attributes) =>
        ({ chain }) => {
          // map MediaAssetOrExternalImage item to LinkContent
          // set defaults for nullable values
          // extract the LinkContent value, as we don't need the __TYPE__: "LinkContent" and only care about the media properties
          const media = stringifyMediaLinkData(
            mapMediaToLinkContent({
              media: attributes.media,
            }).value,
          )

          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMark(this.name, {
              media,
            })
            .setMeta("preventAutolink", true)
            .run()
        },
      unsetLink:
        () =>
        ({ chain }) => {
          return chain()
            .unsetMark(this.name, { extendEmptyMarkRange: true })
            .setMeta("preventAutolink", true)
            .run()
        },
    }
  },
})

const converter = {
  fromPrismic(schema: Schema) {
    return function (elmt: LinkElement): ProsemirrorMark | undefined {
      const data = elmt.data
      if (data.__TYPE__ === "DocumentLink") {
        const attrs = {
          type: TTLink.name,
          id: data.id,
        }

        return schema.marks[TTLink.name]?.create(attrs)
      }

      if (data.__TYPE__ === "ImageLink") {
        const attrs = {
          type: TTLink.name,
          media: stringifyMediaLinkData(data),
        }

        return schema.marks[TTLink.name]?.create(attrs)
      }

      if (data.__TYPE__ === "FileLink") {
        const attrs = {
          type: TTLink.name,
          media: stringifyMediaLinkData(data),
        }

        return schema.marks[TTLink.name]?.create(attrs)
      }

      if (data.__TYPE__ === "ExternalLink") {
        const attrs = {
          type: TTLink.name,
          url: data.url,
          href: data.url,
          target: data.target,
        }

        return schema.marks[TTLink.name]?.create(attrs)
      }
      return
    }
  },

  toPrismic(link: LinkMark): LinkElementData {
    if (link.attrs.media) {
      return parseMediaLinkData(link.attrs.media)
    }

    if (link.attrs.id) {
      return {
        __TYPE__: "DocumentLink",
        id: link.attrs.id,
      }
    }

    return {
      __TYPE__: "ExternalLink",
      url: link.attrs.href,
      target: link.attrs.target,
    }
  },
}

export interface LinkMarkAttributes extends Attrs {
  href: string
  id: string
  target: string
  media?: string
}

interface LinkMark extends ProsemirrorMark {
  attrs: LinkMarkAttributes
}

export const Link = new LinkExtension(componentName, TTLink, converter)

export function stringifyMediaLinkData(media: ImageLinkData | FileLinkData) {
  return JSON.stringify(media)
}

export function parseMediaLinkData(media: string) {
  return JSON.parse(media) as LinkElementData
}
